import PptxGenJS from "pptxgenjs";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import atlasApi from "./atlasapi";

const COUNTRY_ORDER = ["Bulgaria"];
const ENERGY_TYPES = ["Wind", "Solar"];

const TABLE_COLUMNS = [
	{ header: "Project ID", key: "projectid" },
	{ header: "Name", key: "name" },
	{ header: "Price region", key: "price_region" },
	{ header: "Status", key: "phase" },
	{ header: "Year", key: "vintage" },
	{ header: "Signed contracts (last month)", key: "compare_signed_contracts" },
	{ header: "Signed contracts", key: "signed_contracts" },
	{ header: "Project potential MW", key: "project_potential_mw" },
	{ header: "Signed capacity (last month)", key: "compare_signed_capacity_mw" },
	{ header: "Signed capacity MW", key: "signed_capacity_mw" },
	{ header: "M1 approved MW", key: "capacity_approved_mw" },
];

function formatNumber(num) {
	if (num == null) return "\u2014";
	return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function filterProjects(projects) {
	return (projects || [])
		.filter((p) => p.phase === "Landowner agreement" || p.phase === "Permitting")
		.map((p) =>
			p.state === "On hold" || p.state === "Terminated" ? { ...p, phase: "Terminated" } : p
		);
}

function buildTotalsRow(projects) {
	if (!projects.length) return null;
	return {
		_isTotal: true,
		projectid: "",
		name: "Total",
		price_region: "",
		phase: "",
		vintage: "",
		compare_signed_contracts: projects.reduce((s, p) => s + (p.compare_signed_contracts || 0), 0),
		signed_contracts: projects.reduce((s, p) => s + (p.signed_contracts || 0), 0),
		project_potential_mw: projects.reduce((s, p) => s + (p.project_potential_mw || 0), 0),
		compare_signed_capacity_mw: projects.reduce((s, p) => s + (p.compare_signed_capacity_mw || 0), 0),
		signed_capacity_mw: projects.reduce((s, p) => s + (p.signed_capacity_mw || 0), 0),
		capacity_approved_mw: projects.reduce((s, p) => s + (p.capacity_approved_mw || 0), 0),
	};
}

function buildCombos() {
	const combos = [];
	for (const country of COUNTRY_ORDER) {
		for (const energy of ENERGY_TYPES) {
			combos.push({ country, energy });
		}
	}
	return combos;
}

export async function fetchAllMetricsData() {
	await atlasApi.getToken();
	const combos = buildCombos();
	const results = await Promise.all(
		combos.map(({ country, energy }) => atlasApi.getMetricsProjects(country, energy))
	);
	return combos.map((combo, i) => ({
		...combo,
		label: `${combo.country} - ${combo.energy}`,
		raw: results[i],
		filtered: filterProjects(results[i]?.projects),
	}));
}

function prepareTableData(entry) {
	const filtered = entry.filtered;
	const totals = buildTotalsRow(filtered);
	return { rows: filtered, totals, label: entry.label };
}

export async function captureDashboardImage() {
	const el = document.querySelector(".metrics-dashboard");
	if (!el) throw new Error("Dashboard element not found");
	const canvas = await html2canvas(el, { scale: 2, useCORS: true });
	return canvas.toDataURL("image/png");
}

// ─── PowerPoint Export ───

export async function exportMetricsPowerPoint(allData, chartImage) {
	const pptx = new PptxGenJS();
	pptx.defineLayout({ name: "WIDE", width: 13.33, height: 7.5 });
	pptx.layout = "WIDE";

	// Slide 1: Dashboard chart image
	const slide1 = pptx.addSlide();
	slide1.addText("Metrics Dashboard", { x: 0.5, y: 0.2, fontSize: 20, bold: true, color: "2c3e5a" });
	if (chartImage) {
		slide1.addImage({ data: chartImage, x: 0.3, y: 0.8, w: 12.7, h: 6.4, sizing: { type: "contain", w: 12.7, h: 6.4 } });
	}

	// Slides 2-9: Tables
	for (const entry of allData) {
		const { rows, totals, label } = prepareTableData(entry);
		const slide = pptx.addSlide();
		slide.addText(label, { x: 0.5, y: 0.2, fontSize: 18, bold: true, color: "2c3e5a" });

		const headerRow = TABLE_COLUMNS.map((c) => ({
			text: c.header,
			options: { bold: true, fontSize: 8, fill: "2c3e5a", color: "FFFFFF", align: "center" },
		}));

		const dataRows = rows.map((r) =>
			TABLE_COLUMNS.map((c) => {
				const val = r[c.key];
				return { text: typeof val === "number" ? formatNumber(val) : String(val ?? ""), options: { fontSize: 7, align: "center" } };
			})
		);

		if (totals) {
			dataRows.push(
				TABLE_COLUMNS.map((c) => {
					const val = totals[c.key];
					return {
						text: typeof val === "number" ? formatNumber(val) : String(val ?? ""),
						options: { fontSize: 7, bold: true, align: "center", fill: "E8EDF2" },
					};
				})
			);
		}

		const colW = [0.7, 1.8, 0.6, 1.2, 0.5, 1.0, 0.9, 1.0, 1.0, 1.0, 0.9];
		slide.addTable([headerRow, ...dataRows], {
			x: 0.3,
			y: 0.8,
			colW,
			border: { pt: 0.5, color: "CCCCCC" },
			rowH: 0.3,
		});
	}

	const blob = await pptx.write({ outputType: "blob" });
	saveAs(blob, "metrics-export.pptx");
}

// ─── Excel Export ───

export async function exportMetricsExcel(allData) {
	const workbook = XLSX.utils.book_new();

	for (const entry of allData) {
		const { rows, totals, label } = prepareTableData(entry);

		// Build array of arrays: header row + data rows + totals row
		const headers = TABLE_COLUMNS.map((c) => c.header);
		const dataArr = rows.map((r) =>
			TABLE_COLUMNS.map((c) => r[c.key] ?? "")
		);
		if (totals) {
			dataArr.push(TABLE_COLUMNS.map((c) => totals[c.key] ?? ""));
		}

		const sheetData = [headers, ...dataArr];
		const ws = XLSX.utils.aoa_to_sheet(sheetData);

		// Set column widths
		ws["!cols"] = TABLE_COLUMNS.map((c) => ({
			wch: c.key === "name" ? 25 : c.key === "phase" ? 18 : 15,
		}));

		XLSX.utils.book_append_sheet(workbook, ws, label);
	}

	const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
	saveAs(
		new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
		"metrics-export.xlsx"
	);
}

// ─── PDF Export ───

export async function exportMetricsPDF(allData, chartImage) {
	const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

	// Page 1: Dashboard chart
	doc.setFontSize(18);
	doc.setTextColor(44, 62, 90);
	doc.text("Metrics Dashboard", 15, 15);
	if (chartImage) {
		doc.addImage(chartImage, "PNG", 10, 22, 275, 165);
	}

	// Pages 2-9: Tables
	const headers = TABLE_COLUMNS.map((c) => c.header);

	for (const entry of allData) {
		const { rows, totals, label } = prepareTableData(entry);
		doc.addPage();
		doc.setFontSize(14);
		doc.setTextColor(44, 62, 90);
		doc.text(label, 15, 15);

		const body = rows.map((r) =>
			TABLE_COLUMNS.map((c) => {
				const val = r[c.key];
				return typeof val === "number" ? formatNumber(val) : String(val ?? "");
			})
		);

		if (totals) {
			body.push(
				TABLE_COLUMNS.map((c) => {
					const val = totals[c.key];
					return typeof val === "number" ? formatNumber(val) : String(val ?? "");
				})
			);
		}

		autoTable(doc, {
			head: [headers],
			body,
			startY: 22,
			styles: { fontSize: 7, cellPadding: 2 },
			headStyles: { fillColor: [44, 62, 90], textColor: 255, fontStyle: "bold", halign: "center" },
			columnStyles: {
				0: { cellWidth: 15 },
				1: { cellWidth: 35 },
				2: { cellWidth: 15 },
				3: { cellWidth: 25 },
				4: { cellWidth: 12 },
			},
			didParseCell: (data) => {
				// Bold totals row (last row)
				if (totals && data.row.index === body.length - 1) {
					data.cell.styles.fontStyle = "bold";
					data.cell.styles.fillColor = [232, 237, 242];
				}
			},
		});
	}

	doc.save("metrics-export.pdf");
}
